/**
 * Minimal WKT parser for the two shapes molluskio publishes for parcels:
 * POLYGON and MULTIPOLYGON, EPSG:4326. Returns null for anything else.
 *
 * Why hand-roll: a full WKT parser ships ~30 KB; we only need this one
 * code path and the shapes are simple enough.
 */
type Polygon = { type: 'Polygon'; coordinates: number[][][] };
type MultiPolygon = { type: 'MultiPolygon'; coordinates: number[][][][] };
export type ParcelGeometry = Polygon | MultiPolygon;

const POLY_RE = /^\s*POLYGON\s*\((.*)\)\s*$/i;
const MULTI_RE = /^\s*MULTIPOLYGON\s*\((.*)\)\s*$/i;

export function wktToGeoJSON(wkt: string): ParcelGeometry | null {
  const polyMatch = POLY_RE.exec(wkt);
  if (polyMatch) {
    const rings = parseRings(polyMatch[1] ?? '');
    return rings ? { type: 'Polygon', coordinates: rings } : null;
  }
  const multiMatch = MULTI_RE.exec(wkt);
  if (multiMatch) {
    const inner = multiMatch[1] ?? '';
    const polys: number[][][][] = [];
    // Split top-level "((...),(...))" groups by matching parens depth.
    let depth = 0;
    let start = -1;
    for (let i = 0; i < inner.length; i++) {
      const c = inner[i];
      if (c === '(') {
        if (depth === 0) start = i + 1;
        depth++;
      } else if (c === ')') {
        depth--;
        if (depth === 0 && start !== -1) {
          const rings = parseRings(inner.slice(start, i));
          if (rings) polys.push(rings);
          start = -1;
        }
      }
    }
    return polys.length ? { type: 'MultiPolygon', coordinates: polys } : null;
  }
  return null;
}

/**
 * Ray-casting point-in-polygon test against a single ring. Works in
 * planar lng/lat space, which is accurate enough at parcel scale.
 */
function pointInRing(point: [number, number], ring: number[][]): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if (!a || !b || a.length < 2 || b.length < 2) continue;
    const xi = a[0] as number;
    const yi = a[1] as number;
    const xj = b[0] as number;
    const yj = b[1] as number;
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInPolygon(point: [number, number], rings: number[][][]): boolean {
  const outer = rings[0];
  if (!outer || !pointInRing(point, outer)) return false;
  // Subtract any holes.
  for (let i = 1; i < rings.length; i++) {
    const hole = rings[i];
    if (hole && pointInRing(point, hole)) return false;
  }
  return true;
}

export function pointInGeometry(point: [number, number], geom: ParcelGeometry): boolean {
  if (geom.type === 'Polygon') return pointInPolygon(point, geom.coordinates);
  return geom.coordinates.some((poly) => pointInPolygon(point, poly));
}

function parseRings(body: string): number[][][] | null {
  const rings: number[][][] = [];
  const ringRe = /\(([^()]*)\)/g;
  let m: RegExpExecArray | null;
  // biome-ignore lint/suspicious/noAssignInExpressions: idiomatic regex loop
  while ((m = ringRe.exec(body)) !== null) {
    const ring = (m[1] ?? '')
      .split(',')
      .map((pair) => pair.trim().split(/\s+/).map(Number))
      .filter((c) => c.length >= 2 && c.every((n) => Number.isFinite(n)));
    if (ring.length) rings.push(ring);
  }
  return rings.length ? rings : null;
}
